<template>
  <div :class="['le-select', `le-select-type-${type}`, { 'is-open': isListOpen }, { 'open-list' : this.listAlwaysOpen }, theme]"
       :id="id"
       :aria-labelledby="ariaLabelledBy"
       :aria-expanded="isListOpen"
       :aria-owns="getComboId"
       :role="role"
       ref="dropdownWrapper"
       aria-haspopup="listbox"
       tabindex="0"
       @keydown.enter.space="onKeydownEnter"
       @keydown.down.right.stop.prevent="onKeydownDown"
       @keydown.up.left.stop.prevent="onKeydownUp"
       @keydown.esc.stop.prevent="onKeydownEsc"
       @keydown.shift.tab="onClickOutside"
       v-click-outside="onClickOutside"
       >
    <div aria-live="polite" class="a11y_live_region">{{arrowKeyText}}</div>

    <le-tags
      v-if="isMultiSelect"
      ref="tagsInput"
      tabindex="-1"
      :tabbable="isListOpen"
      :tags="selectedItems"
      :placeholder="placeholder"
      :searchTerm="searchTerm"
      :tagIdAttr="itemIdAttr"
      :tagValueAttr="itemValueAttr"
      :errorPlacement="errorPlacement"
      :enabled="enabled"
      :controlId="getComboId"
      @focus="onListClick"
      @input="onSearchInputChange"
      @unselect="onUnselectItem"
      @tabout="onClickOutside">
    </le-tags>

    <le-input-field
      v-if="isSingleSelect"
      ref="singleSelectInput"
      tabindex="-1"
      :errorPlacement="errorPlacement"
      :placeholder="placeholder"
      :value="searchTerm"
      :enabled="enabled"
      class="input-field"
      @focus="onListClick"
      @input="onSearchInputChange">
    </le-input-field>

    <div v-if="listAlwaysOpen" class="space"></div>

    <le-select-dropdown
      v-if="isDropdown"
      ref="dropdownInput"
      tabindex="-1"
      :selectedItem="selectedItems[0]"
      :itemIconClass="selectedItems[0] && selectedItems[0].class"
      :itemValueAttr="itemValueAttr"
      :isListOpen="isListOpen"
      :useIcons="useIcons"
      :useIconsAndText="useIconsAndText"
      :enabled="enabled"
      :errorPlacement="errorPlacement"
      :container="container"
      :placeholder="placeholder"
      @toggle="onDropdownToggle">
    </le-select-dropdown>

    <le-select-menu
      v-if="isActionMenu"
      :isListOpen="isListOpen"
      :enabled="enabled"
      @toggle="onActionMenuToggle">
    </le-select-menu>

    <le-select-list
      v-show="isListOpen && enabled"
      ref="itemsList"
      role="listbox"
      :id="getComboId"
      :aria-multiselectable="isMultiSelect"
      :class="['select-list', theme]"
      :items="filteredItems"
      :itemIdAttr="itemIdAttr"
      :itemValueAttr="itemValueAttr"
      :itemTooltipAttr="itemTooltipAttr"
      :itemSubtextAttr="itemSubtextAttr"
      :groupSelection="groupSelectEnable"
      :hasShortcuts="listWithShortcuts"
      :isMultiSelect="isMultiSelect"
      :selectedItemsIds="selectedItemsIds"
      :listMaxHeight="heightLimit"
      :itemMinHeight="itemMinHeight"
      :pointedItemIndex="pointedItemIndex"
      :floatedList="floatedList"
      :openListOnTop="shouldOpenListOnTop"
      :hasIconsOnly="useIcons"
      :hasIconsAndText="useIconsAndText"
      :bubbleList="bubbleList"
      :bubbleListAlign="bubbleListAlign"
      :addItemsSeparator="addItemsSeparator"
      :minWidth="listMinWidth"
      :isOpenList="listAlwaysOpen"
      :searchTerm="searchTerm"
      @scroll="onListScroll"
      @itemClicked="onListItemClicked"
      @groupClicked="onListGroupClicked"
      @itemHover="onListItemHover"
      @groupHover="onListGroupHover"
      @tabout="onClickOutside"
      @onItemCreateClick="createNewItem">
    </le-select-list>
  </div>
</template>

<script>
  import {
    forEach,
    filter,
    isEmpty,
    map,
    includes,
    indexOf,
    throttle,
    find,
    get,
    values,
    isNil,
    uniqueId,
    isFunction,
    isNumber,
    isArray,
  } from 'lodash';
  import leTags from '../le-tags/le-tags';
  import leSelectList from './le-select-list';
  import leInputField from '../le-input-field/le-input-field';
  import leSelectDropdown from './le-select-dropdown';
  import leSelectMenu from './le-select-menu';
  import clickOutside from '../../../directives/clickOutsideDirective';
  import CONSTS from '../../../consts/consts';

  export default {
    name: 'le-select',
    components: {
      leSelectList,
      leTags,
      leInputField,
      leSelectDropdown,
      leSelectMenu,
    },
    directives: {
      clickOutside,
    },
    props: {
      id: {
        type: String,
      },
      ariaLabelledBy: {
        type: String,
      },
      // Either multiselect, singleselect or dropdown.
      type: {
        required: true,
        validator: type => indexOf(values(CONSTS.LE_SELECT_CONSTS.TYPE), type) > -1,
      },
      theme: {
        type: String,
        default: '',
      },
      // List of items
      items: {
        type: Array,
        required: true,
      },
      // This is the selected items array. used to support binding from outside with v-model.
      value: {
        type: [Array, Object],
        default: () => [],
      },
      // Placeholder when nothing is selected
      placeholder: {
        type: String,
        default: 'Select <value>',
      },
      groupIdAttr: {
        type: String,
        default: 'id',
      },
      // The field of the group label
      groupValueAttr: {
        type: String,
      },
      // The field of the group items
      groupItemsAttr: {
        type: String,
      },
      // The field of the group items
      groupSelectEnable: {
        type: Boolean,
        default: false,
      },
      // The field of the items key
      itemIdAttr: {
        type: String,
        default: 'id',
      },
      // The field of the itemx value
      itemValueAttr: {
        type: String,
        default: 'value',
      },
      // The field of the subtext
      itemSubtextAttr: {
        type: String,
        default: 'subtext',
      },
      itemTooltipAttr: {
        type: String,
        default: 'subtext',
      },
      // Size of a page. Must be greater than maxItemsToShow.
      pageSize: {
        type: Number,
        default: 21,
      },
      // Max items to show before we see a scroller.
      maxItemsToShow: {
        type: Number,
        default: 20,
      },
      // Height in pixels for a buffer in the bottom of the list. Scrolling to the buffer will load the next page.
      loadMoreOffset: {
        type: Number,
        default: 20,
      },
      // Minimal height of each item.
      itemMinHeight: {
        type: Number,
        default: 32,
      },
      // If the list is floating (position: absolute) or not.
      floatedList: {
        type: Boolean,
        default: true,
      },
      // Available only when the list is floating.
      openListOnTop: {
        type: Boolean,
        default: false,
      },
      // If to close the list after selection. relevant only for multiselect.
      closeOnSelect: {
        type: Boolean,
      },
      // If to support creating items that do not exist in the list
      allowItemCreation: {
        type: Boolean,
      },
      itemCreationLabel: {
        type: String,
        default: '+ Create item',
      },
      // A custom function for the search.
      searchFunc: {
        type: Function,
        default: (items, searchTerm, itemValueAttr) => filter(items, item => includes(item[itemValueAttr].toLowerCase().trim(), searchTerm.toLowerCase().trim())),
      },
      isGroupSupported: {
        type: Boolean,
      },
      noItemsLabel: {
        type: String,
        default: 'No matching value',
      },
      // is dropdown need to select item in default
      dropdownDefaultSelection: {
        type: Boolean,
      },
      displayErrors: {
        type: Boolean,
        default: true,
      },
      errorPlacement: {
        type: String,
      },
      // Fixes a bug with v-popover, that uses requestAnimationFrame after detecting a click.
      // if we remove an element before the v-popover finishes to handle it's click event, it will think we clicked outside and close itself.
      // Therefore we add it here also.
      requestAnimationFrame: {
        type: Boolean,
        default: true,
      },
      // Only supported by dropdown. Pass the class for the <i> tag into the valueAttr.
      useIcons: {
        type: Boolean,
      },
      // Only supported by dropdown. Pass the class for the <i> tag alongside the value, in a class property.
      useIconsAndText: {
        type: Boolean,
      },
      // Bubble permutation for the le-select-list
      bubbleList: {
        type: Boolean,
        default: false,
      },
      bubbleListAlign: {
        type: String,
        default: CONSTS.LE_SELECT_CONSTS.BUBBLE_LIST_ALIGN.RIGHT,
      },
      // Add items separator for the list
      addItemsSeparator: {
        type: Boolean,
        default: false,
      },
      listMinWidth: {
        type: String,
        default: '100%',
      },
      listAlwaysOpen: {
        type: Boolean,
        default: false,
      },
      listWithShortcuts: {
        type: Boolean,
        default: false,
      },
      enabled: {
        type: Boolean,
        default: true,
      },
      container: {
        type: String,
      },
      // In order to remove the selection in dropdown
      shouldAddPlaceholderItem: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        searchTerm: '',
        isListOpen: this.listAlwaysOpen,
        currentPage: 1,
        pointedItemIndex: -1,
        error: null,
        preferredOpenDirection: 'bottom',
        comboId: uniqueId('combo-'),
        arrowKeyText: '',
      };
    },
    created() {
      if (this.isDropdown && !this.value && this.dropdownDefaultSelection) {
        this.$emit('input', this.filteredItems[0]);
      }
      if (this.isSingleSelect && this.selectedItems.length > 0) {
        this.searchTerm = this.selectedItems[0][this.itemValueAttr];
      }
    },
    computed: {
      heightLimit() {
        let listHeight = this.maxItemsToShow * this.itemMinHeight;
        if (this.pageSize === this.maxItemsToShow) {
          listHeight -= 11; // To show a scroll
        }
        return listHeight;
      },
      filteredItems() {
        let items;
        if (this.isGroupSupported) {
          items = this.filterAndFlat(this.items, this.searchTerm);
        } else {
          items = this.filterItems(this.items, this.searchTerm);
        }

        this.addItemCreationItem(items);
        this.addPlaceholderItemToList(items);
        this.addNoItemsLabel(items);

        if (this.pageSize) {
          items = items.slice(0, this.pageSize * this.currentPage);
        }
        return items;
      },
      selectedItems() {
        if (Array.isArray(this.value)) {
          return this.value;
        }
        return isNil(this.value) ? [] : [this.value];
      },
      selectedItemsIds() {
        return map(this.selectedItems, item => item && item[this.itemIdAttr]);
      },
      pointedElementOffsetTop() {
        return this.pointedItemIndex * this.itemMinHeight;
      },
      shouldOpenListOnTop() {
        return this.floatedList && (this.preferredOpenDirection === 'top' || this.openListOnTop);
      },
      isMultiSelect() {
        return this.type === CONSTS.LE_SELECT_CONSTS.TYPE.MULTISELECT;
      },
      isSingleSelect() {
        return this.type === CONSTS.LE_SELECT_CONSTS.TYPE.SINGLESELECT;
      },
      isDropdown() {
        return this.type === CONSTS.LE_SELECT_CONSTS.TYPE.DROPDOWN;
      },
      isActionMenu() {
        return this.type === CONSTS.LE_SELECT_CONSTS.TYPE.MENU;
      },
      isListOnly() {
        return this.type === CONSTS.LE_SELECT_CONSTS.TYPE.LIST_ONLY;
      },
      hasSelectedItems() {
        return !isEmpty(this.selectedItemsIds);
      },
      isExpanded() {
        return this.isListOpen ? 'true' : 'false';
      },
      role() {
        return this.isMultiSelect ? 'combobox' : 'button';
      },
      getComboId() {
        return this.isMultiSelect ? this.comboId : null;
      },
    },
    watch: {
      searchTerm() {
        this.$emit('search-change', this.searchTerm);
        this.resetPointedItemIndex();
      },
      isListOpen() {
        if (this.isListOpen) {
          this.resetPointedItemIndex();
          this.$nextTick(() => {
            this.$refs.itemsList.$el.scrollTop = 0;
          });
        }
      },
    },
    methods: {
      addItemCreationItem(items) {
        if (this.allowItemCreation && this.searchTerm.trim() &&
          !find(items, item => !item[this.itemValueAttr].isGroupLabel && item[this.itemValueAttr].toLowerCase().trim() === this.searchTerm.toLowerCase().trim())) {
          items.unshift({
            [this.itemIdAttr]: 'leSelectItemCreation',
            [this.itemValueAttr]: this.getItemCreationLabel(),
            isItemCreation: true,
          });
        }
      },
      addNoItemsLabel(items) {
        if (!items.length) {
          items.push({
            [this.itemIdAttr]: 'leSelectNoItemsLabel',
            [this.itemValueAttr]: this.noItemsLabel,
            isNoItemsLabel: true,
          });
        }
      },
      addPlaceholderItemToList(items) {
        if (this.isDropdown && this.hasSelectedItems && this.shouldAddPlaceholderItem) {
          items.unshift({
            [this.itemIdAttr]: 'leSelectPlaceholderItem',
            [this.itemValueAttr]: this.placeholder,
            isPlaceholderItem: true,
          });
        }
      },
      showList() {
        if (this.isListOpen) {
          return;
        }

        this.adjustPosition();

        this.isListOpen = true;

        this.$nextTick(() => {
          if (!(this.isSingleSelect || this.isMultiSelect)) {
            this.$refs.itemsList.$el.focus();
          }
        });
      },
      hideList() {
        if (!this.isListOpen || this.listAlwaysOpen) {
          return;
        }

        this.isListOpen = false;
      },
      // eslint-disable-next-line
      onListScroll: throttle(function(event) {
        const totalHeight = this.$refs.itemsList.$el.scrollHeight;
        const currentScrollTop = this.$refs.itemsList.$el.scrollTop;
        const currentHeight = this.$refs.itemsList.$el.clientHeight;
        const bottomLine = currentScrollTop + currentHeight;

        if (this.pageSize && totalHeight - bottomLine <= this.loadMoreOffset) {
          this.currentPage = this.currentPage + 1;
        }
      }, 200),
      onClickOutside() {
        if (this.isMultiSelect) {
          this.onMultiselectClickOutside();
        }
        if (this.isSingleSelect) {
          this.onSingleSelectClickOutside();
        }

        this.hideList();
      },
      onMultiselectClickOutside() {
        this.searchTerm = this.searchTerm.trim();
        const firstMatchingItem = find(this.filteredItems, item => !item.isGroupLabel && (item[this.itemValueAttr] === this.searchTerm));

        if (this.displayErrors && this.searchTerm && !firstMatchingItem) {
          const error = this.allowItemCreation ? CONSTS.LE_SELECT_CONSTS.ERRORS.MULTI_SELECT.UNFINISHED_CREATE : CONSTS.LE_SELECT_CONSTS.ERRORS.MULTI_SELECT.INVALID_ITEM;
          this.setError(true, error);
        } else {
          this.setError(false);
        }
      },
      onSingleSelectClickOutside() {
        this.searchTerm = this.searchTerm.trim();
        const firstMatchingItem = find(this.filteredItems, item => !item.isGroupLabel && (item[this.itemValueAttr] === this.searchTerm));

        if (firstMatchingItem) {
          this.selectItem(firstMatchingItem);
          this.setError(false);
        } else {
          this.$emit('input', null);
          if (this.displayErrors && this.searchTerm) {
            this.setError(true, CONSTS.LE_SELECT_CONSTS.ERRORS.SINGLE_SELECT.INVALID_ITEM);
          } else {
            this.setError(false);
          }
        }
      },
      onListClick() {
        if (!this.isListOpen) {
          this.showList();
        }
      },
      onUnselectItem(item) {
        if (this.requestAnimationFrame) {
          requestAnimationFrame(() => {
            this.unselectItem(item);
          });
        } else {
          this.unselectItem(item);
        }
      },
      unselectItem(removedItem) {
        const itemIndex = indexOf(this.selectedItemsIds, removedItem[this.itemIdAttr]);
        const newSelectedItems = this.selectedItems.slice(0, itemIndex).concat(this.selectedItems.slice(itemIndex + 1)); // Creating a new array without the removed item
        this.$emit('input', newSelectedItems);
        this.$emit('unselect', removedItem);
      },
      unselectGroup(removedItemsArray) {
        for (let i = 0; i < removedItemsArray.length; i += 1) {
          const itemIndex = indexOf(this.selectedItemsIds, removedItemsArray[i][this.itemIdAttr]);
          this.selectedItems.splice(itemIndex, 1);
        }
        this.$emit('input', this.selectedItems);
        this.$emit('unselectGroup', removedItemsArray);
      },
      selectItem(item) {
        if (this.requestAnimationFrame) {
          requestAnimationFrame(() => {
            this.doSelectItem(item);
          });
        } else {
          this.doSelectItem(item);
        }
      },
      selectGroup(itemsArray) {
        if (this.requestAnimationFrame) {
          requestAnimationFrame(() => {
            this.doSelectGroup(itemsArray);
          });
        } else {
          this.doSelectGroup(itemsArray);
        }
      },
      doSelectGroup(itemsArray) {
        this.$emit('selectGroup', itemsArray);
        this.searchTerm = '';
        itemsArray.forEach((item) => {
          this.selectedItems.push(item);
        });
        this.$emit('input', this.selectedItems);
      },
      doSelectItem(item) {
        this.$emit('select', item);
        if (this.isMultiSelect) {
          this.$emit('input', this.selectedItems.concat([item]));
          this.searchTerm = '';
        } else { // we might add if(!isActionMenu)
          this.$emit('input', item);
          if (this.isSingleSelect && !this.listAlwaysOpen) {
            this.searchTerm = item[this.itemValueAttr];
          }
        }
      },
      getItemCreationLabel() {
        return `${this.itemCreationLabel} "${this.searchTerm.trim()}"`;
      },
      onListItemHover(index) {
        this.pointedItemIndex = index;
      },
      onListGroupHover(index) {
        if (this.groupSelectEnable) {
          this.pointedItemIndex = index;
        }
      },
      onListItemClicked(item) {
        if (item.isGroupLabel) {
          return;
        }

        this.decideActionOnItem(item);

        if (!this.isMultiSelect || this.closeOnSelect) {
          this.$refs.dropdownWrapper.focus();
          this.hideList();
        }
        if (this.isSingleSelect) {
          this.searchTerm = this.searchTerm.trim();
        }
      },
      onListGroupClicked({ groupItems, groupStatus, selectedGroupItems }) {
        switch (groupStatus) {
          case CONSTS.LE_CHECKBOX.STATES.CHECKED:
          case CONSTS.LE_CHECKBOX.STATES.INDETERMINATE:
            this.unselectGroup(selectedGroupItems);
            break;
          case CONSTS.LE_CHECKBOX.STATES.UNCHECKED:
          default:
            this.selectGroup(this.getSelectedGroupComplement(selectedGroupItems, groupItems));
            break;
        }
      },
      getSelectedGroupComplement(selectedGroupItems, groupItems) {
        const comparison = (itemA, itemB) => itemA[this.itemIdAttr] !== itemB[this.itemIdAttr];
        const result = groupItems.filter(itemB => selectedGroupItems.every(itemA => comparison(itemA, itemB)));
        return result;
      },
      decideActionOnItem(item) {
        if (item.isItemCreation) {
          this.createNewItem();
        } else if (this.isItemSelected(item) && this.isMultiSelect) {
          this.unselectItem(item);
        } else if (item.isPlaceholderItem) {
          const removedItem = this.selectedItems[0];
          this.unselectItem(removedItem);
        } else if (!item.isGroupLabel) {
          this.selectItem(item);
        }
      },
      createNewItem() {
        this.$emit('create', this.searchTerm);
        this.searchTerm = '';
      },
      selectPointedElement() {
        const pointedItem = this.filteredItems[this.pointedItemIndex];
        if (!get(pointedItem, 'disable')) {
          if (this.filteredItems.length > 0 && !get(pointedItem, 'isNoItemsLabel')) {
            this.decideActionOnItem(pointedItem);
          }

          if (!this.isMultiSelect || this.closeOnSelect) {
            this.hideList();
            if (!this.isSingleSelect) {
              this.$refs.dropdownWrapper.focus();
            }
          }
        }
      },
      movePointedElementDown() {
        const isNotLastItem = (this.pointedItemIndex < this.filteredItems.length - 1);
        const isNextHasNotItemsLabel = get(this.filteredItems[this.pointedItemIndex + 1], 'isNoItemsLabel');
        // move down if there is space to move (not the last one) and if the next one is not "there is no items" item
        if (isNotLastItem && !isNextHasNotItemsLabel) {
          this.pointedItemIndex = this.pointedItemIndex + 1;
          const pointedElementScrollTop = this.pointedElementOffsetTop - ((this.maxItemsToShow - 1) * this.itemMinHeight);
          // We check if the new pointed element is below the scroller. if so, scroll to it.
          if (this.$refs.itemsList.$el.scrollTop <= pointedElementScrollTop) {
            this.$refs.itemsList.$el.scrollTop = pointedElementScrollTop;
          }
          if (get(this.filteredItems[this.pointedItemIndex], 'isGroupLabel')) {
            this.movePointedElementDown();
          }
        }
        setTimeout(() => { this.arrowKeyAnnouncement(); }, 500);
      },
      movePointedElementUp() {
        // If the first element is a group, and i try to point to it, don't allow it
        if (this.pointedItemIndex === 1 && get(this.filteredItems[this.pointedItemIndex - 1], 'isGroupLabel')) {
          return;
        }
        if (this.pointedItemIndex >= 0) {
          this.pointedItemIndex = this.pointedItemIndex - 1;
          if (this.$refs.itemsList.$el.scrollTop >= this.pointedElementOffsetTop) {
            this.$refs.itemsList.$el.scrollTop = this.pointedElementOffsetTop;
          }
          if (get(this.filteredItems[this.pointedItemIndex], 'isGroupLabel')) {
            this.movePointedElementUp();
          }
        }
        setTimeout(() => { this.arrowKeyAnnouncement(); }, 500);
      },
      arrowKeyAnnouncement() {
        if (this.$el && isFunction(this.$el.querySelector)) {
          const pointedElement = this.$el.querySelector('.pointed');
          const selectedElement = this.$el.querySelector('.selected');
          let itemText = '';
          if (pointedElement) {
            itemText = pointedElement.innerText;
          } else if (selectedElement) {
            itemText = selectedElement.innerText;
          }
          if (itemText !== '' && isNumber(this.pointedItemIndex) && isArray(this.filteredItems)) {
            this.arrowKeyText = `${itemText} option ${(this.pointedItemIndex + 1)} of ${(this.filteredItems.length)}`;
          }
        }
      },
      resetPointedItemIndex() {
        this.pointedItemIndex = -1;
      },
      filterAndFlat(items, searchTerm) {
        const filteredItems = this.filterGroups(items, searchTerm);
        return this.flattenItems(filteredItems);
      },
      filterItems(items, searchTerm) {
        return this.searchFunc(items, searchTerm, this.itemValueAttr);
      },
      filterGroups(items, searchTerm) {
        const filteredGroups = [];
        forEach(items, (group) => {
          const groupItems = this.filterItems(group[this.groupItemsAttr], searchTerm);

          if (groupItems.length) {
            filteredGroups.push({
              [this.groupIdAttr]: group[this.groupIdAttr],
              [this.groupValueAttr]: group[this.groupValueAttr],
              [this.groupItemsAttr]: groupItems,
            });
          }
        });
        return filteredGroups;
      },
      flattenItems(items) {
        const flattenedItems = [];
        forEach(items, (group) => {
          if (!isEmpty(group[this.groupItemsAttr])) {
            flattenedItems.push({
              [this.itemIdAttr]: group[this.groupIdAttr],
              [this.itemValueAttr]: group[this.groupValueAttr],
              isGroupLabel: true,
            });
            Array.prototype.push.apply(flattenedItems, group[this.groupItemsAttr]);
          }
        });
        return flattenedItems;
      },
      onSearchInputChange(searchTerm) {
        this.error = null;
        this.searchTerm = searchTerm;
      },
      resetSearchTerm() {
        this.searchTerm = '';
      },
      isItemSelected(item) {
        return indexOf(this.selectedItemsIds, item[this.itemIdAttr]) > -1;
      },
      onDropdownToggle(state) {
        if (state) {
          this.showList();
        } else {
          this.hideList();
        }
      },
      onActionMenuToggle(state) {
        if (state) {
          this.showList();
        } else {
          this.hideList();
        }
      },
      adjustPosition() {
        const spaceAbove = this.$el.getBoundingClientRect().top;
        const spaceBelow = window.innerHeight - this.$el.getBoundingClientRect().bottom;
        const hasEnoughSpaceBelow = spaceBelow > this.heightLimit;
        const hasEnoughSpaceAbove = spaceAbove > this.heightLimit;
        if (!hasEnoughSpaceAbove || hasEnoughSpaceBelow) {
          this.preferredOpenDirection = 'bottom';
        } else {
          this.preferredOpenDirection = 'top';
        }
      },
      setError(showError, error) {
        this.error = showError ? error : '';
        if (this.isSingleSelect) {
          this.$refs.singleSelectInput.setError(showError, this.error);
        }
        if (this.isMultiSelect) {
          this.$refs.tagsInput.setError(showError, this.error);
        }
        if (this.isDropdown) {
          this.$refs.dropdownInput.setError(showError, this.error);
        }
      },
      clearError() {
        if (this.isSingleSelect) {
          this.$refs.singleSelectInput.clearError();
        }
        if (this.isMultiSelect) {
          this.$refs.tagsInput.clearError();
        }
        if (this.isDropdown) {
          this.$refs.dropdownInput.clearError();
        }
        this.error = '';
      },
      getError() {
        return this.error;
      },
      onKeydownDown() {
        this.movePointedElementDown();
      },
      onKeydownUp() {
        this.movePointedElementUp();
      },
      onKeydownEnter(e) {
        if (!this.isListOpen) {
          this.onDropdownToggle(true);
        } else if (this.isSingleSelect) {
          if (this.pointedItemIndex === -1) {
            return;
          }
          this.selectPointedElement();
        } else if (this.isMultiSelect) {
          if (this.pointedItemIndex === -1) {
            return;
          }
          this.selectPointedElement();
          e.preventDefault();
          e.stopPropagation();
        } else if (this.pointedItemIndex === -1) {
          this.hideList();
          this.$refs.dropdownWrapper.focus();
        } else {
          this.selectPointedElement();
        }
      },
      onKeydownEsc() {
        this.$refs.dropdownWrapper.focus();
        this.hideList();
      },
    },
  };

</script>

<style lang="scss" scoped>
  .le-select {
    font-family: Roboto;
    position: relative;
    width: 100%;

    .a11y_live_region {
      border: 0;
      clip: rect(0 0 0 0);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px;
    }

    &:focus,
    &:active {
      // @include themify {
      //   box-shadow: inset 0 0 0 2px getColor('a11yFocusIndicator') !important;
      // }
      @include themify {
        box-shadow: 0 0 0 2px getColor('dropdownButtonBorderActive') !important;
      }
    }

    .trigger {
      width: 100%;
    }
    &.le-select-type-menu {
      width: 18px;
    }
    &.is-open:not(.open-list) {
      .le-select-dropdown {
        @include themify {
          border-color: getColor('dropdownButtonBorderActive');
        }
      }
    }
    &.open-list {
      display: flex;
      flex-direction: column;
      height: 100%;
      .select-list {
        flex-grow: 1;
        height: 100%;
        overflow-y: auto;
      }
      .space {
        display: table-row;
        height: 3px;
      }
    }
  }
</style>
